import { claimsModalEditingModes } from './dashboardScreen/components/claimsModal/claimsModal.constants';
import { castToBoolean, normalizeIdentifier } from '../../lib/util';

class FieldStaffAppController {
  constructor(
    $q,
    $scope,
    $rootScope,
    $log,
    $timeout,
    $window,
    $location,
    $translate,
    showClaimsModal,
    showAppVersionUpdateModal,
    showAppVersionUpdateCompleteModal,
    LogService,
    ThemeSwitcherService,
    APIService,
    AppVersionService,
    AnalyticsService,
    LOG_SLOW_REQUEST_THRESHOLD,
    DataSyncService,
    TranslationService,
    LanguageService,
    RecapResponseService,
    PartialResponseService,
    StaffUserService,
    showConfirmModal,
    MerchandisingRecapScopeService
  ) {
    "ngInject";
    this.$q = $q;
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.$log = $log;
    this.$timeout = $timeout;
    this.$window = $window;
    this.$location = $location;
    this.$translate = $translate;
    this.showClaimsModal = showClaimsModal;
    this.showAppVersionUpdateModal = showAppVersionUpdateModal;
    this.showAppVersionUpdateCompleteModal = showAppVersionUpdateCompleteModal;
    this.LogService = LogService;
    this.APIService = APIService;
    this.AppVersionService = AppVersionService;
    this.AnalyticsService = AnalyticsService;
    this.LOG_SLOW_REQUEST_THRESHOLD = LOG_SLOW_REQUEST_THRESHOLD;
    this.DataSyncService = DataSyncService;
    this.TranslationService = TranslationService;
    this.LanguageService = LanguageService;
    this.PartialResponseService = PartialResponseService;
    this.RecapResponseService = RecapResponseService;
    this.StaffUserService = StaffUserService;
    this.MerchandisingRecapScopeService = MerchandisingRecapScopeService;
    this.editingRecapResponse = null;
    this.editingProductsPosition = null;
    this._loading = false;
    this._updating = false;
    this._checkingVersion = false;
    this._versionCheckPromise = null;
    this.refreshIconDegreesRotation = 0;
    this._platform = null;
    this.ThemeSwitcherService = ThemeSwitcherService;
    this.showConfirmModal = showConfirmModal;
    this._isOfflineMode = false;
  }

  $onInit() {
    const promises = [];

    this.AnalyticsService.trackEvent('APP_LOAD');

    this._setupSlowRequestLogging();

    document.addEventListener(
      "deviceready",
      () => {
        this._platform = device.platform;
      },
      false
    );

    // A rep who keeps the app in the background for weeks may never relaunch
    // it, so resuming is a use that has to slide the session too (#5489).
    document.addEventListener(
      "resume",
      () => {
        this.DataSyncService.refreshTokenIfStale();
      },
      false
    );


    /**
     * Load built-in languages
     */
    promises.push(this.LanguageService.initialize());

    this.$rootScope.$on('showRecapResponseForPosition', (e, position) => {
      const positionEventsRecapsId = position && position.event && position.event.events_recaps_id;
      const recapResponseHasCorrectRecapDefinition = recapResponseOrPartialResponse => {
        if (!positionEventsRecapsId) {
          this.LogService.error("No events_recaps_id found for position", position);
          return false;
        }
        return positionEventsRecapsId
          && recapResponseOrPartialResponse
          && normalizeIdentifier(positionEventsRecapsId) === normalizeIdentifier(recapResponseOrPartialResponse.events_recaps_id);
      };

      return this.$q.when(position.enqueuedRecapResponse)
        .then(result => {
          // No queued recap is found
          if (!result) {

            // Is there queued partial recap?

            if (position.enqueuedPartialResponse && recapResponseHasCorrectRecapDefinition(position.enqueuedPartialResponse)) {
              return position.enqueuedPartialResponse;
            }

                // No queued partial

                // Is there a partial recap?  Use that if there is.
                // If not, is there a saved recap? Use that.
                // If none of the above load a blank one.

                // Is there a saved parital?
                return this.PartialResponseService.getPartialResponses(position)
                  .then( response => {

                    // If we ever want to do multiple available partials the API should return an array and
                    // we would look for data.length > 1 and choose the appropriate one
                    if (response && response.data && recapResponseHasCorrectRecapDefinition(response.data)) {
                      result = this.RecapResponseService.createFromDataForPosition(position, response.data);
                      return result;

                    } else {

                      // No saved partial.
                      // 1. See if position already has recapResponse attached.
                      // 2. Attempt to load recap response for this position.
                      // 3. Create a fresh recap response.

                      // 1. position.recapResponse
                      if (position.recapResponse && position.recapResponse.id && recapResponseHasCorrectRecapDefinition(position.recapResponse)) {
                        return position.recapResponse;
                      }

                      // 2. RecapResponseService.getForPosition()
                      return this.RecapResponseService.getForPosition(position, this.StaffUser)
                        .then(response => {
                          // The saved recap exists
                          if (response && recapResponseHasCorrectRecapDefinition(response)) {
                            return response;
                          } else {
                            // 3. Create fresh recap response
                            result = this.RecapResponseService.getOrCreateForPosition(position);
                            return result;
                          }
                        });
                    }
                  });
          }
        })
        .then(recapResponse => {
          if (!recapResponse.position) {
            recapResponse.position = position
          }

          // Overlay the merchandising recap scope so the per-instance
          // activity-type/product pickers render and default instances seed -
          // mirrors the admin recap controller. The scope is synced offline on
          // the event (event.merchandising_recap_scope), so no live API call is
          // needed and this works offline.
          const event = (position && position.event) || (recapResponse && recapResponse.position && recapResponse.position.event);
          const merchandisingScope = this.MerchandisingRecapScopeService.getFromEvent(event);
          this.MerchandisingRecapScopeService.applyOverlay(recapResponse, merchandisingScope);
          if (typeof recapResponse.updateDisplayLogic === 'function') {
            recapResponse.updateDisplayLogic();
          }
          if (typeof recapResponse.updateLogicModifiers === 'function') {
            recapResponse.updateLogicModifiers();
          }

          this.editingRecapResponse = recapResponse;
          this.editingRecapResponsePosition = position;

          /*
           * Publish a message to the Analytics Service indicating that we are loading this recap response.
           * Include:
           * - recap response ID
           * - recap response version
           * - recap definition ID
           * - recap definition version
           * - event ID
           * - venue ID
           * - event LV's (full objects) and ID's (attached to event via venue, on API)
           * - preview LV's and ID's (replaces event)
           * - recap definition LV's and ID's
           */
          const eventName = 'SHOW_RECAP_RESPONSE_FOR_POSITION';
          return this.$q.when(recapResponse)
            .then(recapResponse => this.$q.when(this.AnalyticsService.trackEvent(
              eventName,
              this.RecapResponseService.getFullDiagnosticInfoForRecapResponse(recapResponse)
            )))
            .catch(err => {
              this.LogService.error(`Swallowing error sending ${eventName}:`, err);
            })
            .finally(() => this.recapResponse);
        });

    });

    this.$rootScope.$on('showProductsForPosition', (e, position) => {
      this.editingProductsPosition = position;
    });

    this.$scope.$on('showEditProfile', () => {
      this.showEditProfile = true;
    });
    this.$scope.$on('hideEditProfile', () => {
      this.showEditProfile = false;
    });

    this.$scope.$on('logout', () => {
      return this.DataSyncService.logout()
        .catch(err => {
          this.LogService.error("Error logging out:", err);
          return this.$q.reject(err);
        })
        .then(() => {
          this.LogService.info("Successfully logged out");
        });
    });

    this.$scope.$on('checkForUpdate', () => {
      return this.checkForUpdate();
    });

    this.$rootScope.$on('claimsModalClosed', (event, ...params) => {
      this.$scope.$broadcast('claimsModalClosed', ...params);

      // Also trigger a data sync, if it's needed
      // This will cause a sync to happen with recap/claim joint submission, when a user submits recap and then exits
      // out of claim (MJOL-919)
      this.DataSyncService.syncIfNeeded();
    });

    this.$scope.$on('LanguageServiceChangedLanguage', () => this._updateLoadingMessage());
    this._loadingMessage = null;

    const tryAutoLogins = () => {
      return this._loginFromSamlRequestId()
        .then(result => {
          return result;
        })
        .catch(err => {
          this.LogService.info("tryAutoLogins: Unable to login using SAML request ID", err);
          return this._loginFromSwitchToKey()
            .then(result => {
              return result;
            })
        })
        .catch(() => {
          return this._restorePreviousLoginSession()
            .then(result => {
              return result;
            })
        });
    };

    this._loading = true;

    const loginAndSyncPromise = this._versionCheckAndUpdate()
      .catch(err => {
        this.LogService.error("Error with version check; swallowing and attempting to login. Error:", err);
        return null;
      })
      .then(result => {
        if (result) {
          return result;
        } else {
          return tryAutoLogins();
        }
      })
      .then(() => {
        return this.DataSyncService.sync()
      })
      .finally(() => {
        this._loading = false;
      });

    promises.push(loginAndSyncPromise);
    return this.$q.all(promises);
  }

  /**
   * Public properties
   */

  set isOnline(val) {
    this._isOnline = val;
  }

  get isOnline() {
    if (this.APIService) {
      return this.APIService.isOnline;
    }
    return false;
  }

  get loading() {
    return this._loading || this._updating || this._checkingVersion;
  }

  get loggedIn() {
    return !!this.staffUser;
  }

  get showDashboard() {
    return !this.loading && this.loggedIn && !this.showRecapResponse && !this.showProducts;
  }

  get showProducts() {
    return !this.loading && !!this.editingProductsPosition;
  }

  get showStatusBar() {
    return !this.showLoading && !this.showLogin;
  }

  get showRecapResponse() {
    return !this.loading && !!this.editingRecapResponse;
  }

  get showLogin() {
    return !this.loading && !this.loggedIn;
  }

  /**
   * Determines whether to use the new iframe-based login flow, or the standard login
   * TODO: Make this config-driven somehow? For testing before we enable the feature
   * @returns {boolean}
   */
  get useServerSideLogin() {
    return true;
  }

  get showLoading() {
    return this.loading;
  }

  set staffUser(staffUser) {
    console.warn(`StaffController set staffUser:`, staffUser);
  }

  get staffUser() {
    return this.DataSyncService && this.DataSyncService.staffUser;
  }

  get cgBusyConfig() {
    if (!this._cgBusyConfig) {
      this.LogService.debug(`cgBusyConfig setting new config object`);
      this._cgBusyConfig = {
        message: this.loadingMessage,
        promise: this.loadingPromises,
      };
    }
    if (this._cgBusyConfig.message !== this.loadingMessage) {
      this.LogService.debug(`cgBusyConfig overwriting loading message`);
      this._cgBusyConfig.message = this.loadingMessage;
    }
    if (this._cgBusyConfig.promise !== this.loadingPromises) {
      this.LogService.debug(`cgBusyConfig overwriting loading promises`);
      this._cgBusyConfig.promise = this.loadingPromises;
    }
    return this._cgBusyConfig;
  }

  get loadingPromises() {
    let promises = [
      this.DataSyncService.syncPromise,
      this.DataSyncService.refreshPositionsPromise,
      this.DataSyncService.refreshDataPromise,
      this.DataSyncService.loginPromise,
      this.DataSyncService.logoutPromise,
      this.DataSyncService.claimsPromise,
      this.StaffUserService.refreshDataPromise,
      this.StaffUserService.refreshPositionsPromise,
      this._versionCheckPromise,
    ].filter(p => !!p);

    let zip = (a1, a2) => a1.map((el, idx) => [el, a2[idx]]);
    let equal = (a1, a2) => {
      if (a1.length !== a2.length) return false;
      return zip(a1, a2).every(([e1, e2]) => e1 === e2);
    };

    if (!this._loadingPromises) {
      this._loadingPromises = [];
    }

    if (!equal(promises, this._loadingPromises)) {
      this._loadingPromises = promises;
    }
    return this._loadingPromises;
  }

  get loadingMessage() {
    if (!this._loadingMessage) {
      this._updateLoadingMessage();
    }
    return this._loadingMessage;
  }

  _updateLoadingMessage() {
    let key;
    if (this._updating) {
      key = 'UPDATING_TO_LATEST_APP_VERSION';
    } else if (this._checkingVersion) {
      key = 'CHECKING_FOR_UPDATES';
    } else {
      key = 'LOADING_DATA';
    }

    if (!this._loadingMessage) {
      this._loadingMessage = key;
    }

    return this.$translate(key)
      .then(translated => {
        this.$timeout(() => {
          this._loadingMessage = translated;
        })
      });
  }

  get loginUsername() {
    return this.DataSyncService && this.DataSyncService.loginUsername;
  }

  get showCheckForUpdate() {
    return this.DataSyncService.isOnline;
  }

  /**
   * Public methods
   */

  exitRecap() {
    this.LogService.log("exitRecap");
    this.editingRecapResponse = null;
    this.$scope.$emit('sync');
  }

  exitProducts() {
    this.LogService.log("exitProducts");
    this.editingProductsPosition = null;
    this.$scope.$emit('sync');
  }

  exitRecapAndSubmitClaimsForRecapResponse(recapResponse) {
    this.editingRecapResponse = null;

    // Tell DataSyncService that we need a sync; this way we'll definitely sync if user exits recap
    // See: MJOL-919
    this.DataSyncService.setNeedsSync();

    const props = {
      position: recapResponse.position,
      event: recapResponse.event,
      staffUser: this.StaffUserService.currentUser,
      editingMode: claimsModalEditingModes.new,
      usePromptRecapCheckoutClaim: castToBoolean(recapResponse.position.event.prompt_recap_checkout_claim),
    };

    return this.showClaimsModal(props)
      .catch(() => this.LogService.log("StaffController openClaims() canceled"));
  }

  checkForUpdate() {
    return this._versionCheckAndUpdate();
  }

  showNetworkSettingsModal() {
    this.$rootScope.$broadcast('showNetworkSettingsModal');
  }

  /**
   * Private methods
   */

  /**
   * Extract saml_request_id from url; fall back to using hash if it's not in $location.search()
   * @returns {null|string}
   * @private
   */
  _getSamlRequestIdFromWindow() {
    const params = this.$location && this.$location.search();
    if (params && params.saml_request_id) {
      return params.saml_request_id;
    } else {
      const hash = this.$location.hash();
      const match = hash && hash.match(/saml_request_id=(.*)$/);
      if (match) {
        return match[1];
      }
    }
    return null;
  }

  /**
   * Attempt to login with SAML request ID, if present
   * @returns {*}
   * @private
   */
  _loginFromSamlRequestId() {
    const samlRequestId = this._getSamlRequestIdFromWindow();
    if (samlRequestId) {
      return this.DataSyncService.loginWithSamlRequestId(samlRequestId);
    }
    return this.$q.reject("Unable to login");
  }

  _loginFromSwitchToKey() {
    const switchToToken = this.$window.nni_staffing_users_jwt_token;
    if (switchToToken) {
      return this.DataSyncService.loginWithToken(switchToToken);
    }

    const switchToLoginKey = this.$location.search().key;
    if (switchToLoginKey !== undefined) {
      let username = false;
      let password = false;
      let URLdecodedKey = decodeURIComponent(switchToLoginKey);
      try {
        let decodedKey = atob(URLdecodedKey);
        decodedKey = decodedKey.split(':');
        username = decodedKey[0];
        password = decodedKey[1];
        return this.DataSyncService.login(username, password);
      } catch (e) {
        this.LogService.error('Unable to decode key; exception:', e);
      }
    }
    return this.$q.reject("Unable to retrieve credentials");
  }

  _restorePreviousLoginSession() {
    return this.DataSyncService.attemptLoginWithExistingCredentials()
      .catch(err => {
        this.LogService.log("Unable to log in existing session. error:", err);
        return this.$q.reject(err);
      });
  }

  _versionCheckAndUpdate() {
    this._versionCheckPromise = this.AppVersionService.versionCheck()
      .then(versionCheckResult => {
        this._checkingVersion = false;
        if (versionCheckResult) {
          // New version found; attempt to upgrade
          return this.showAppVersionUpdateModal()
            .catch(err => {
              this.LogService.debug(`StaffController._versionCheckAndUpdate() not doing version update`);
              return this.$q.reject(err);
            })
            .then(() => {
              this._updating = true;
              return this.AppVersionService.update();
            })
            .then(() => {
              this._updating = false;
              this._checkingVersion = false;
            });
        }
      })
      .catch(err => {
        this.LogService.error("Error checking or updating version:", err);
        this._checkingVersion = false;
        this._updating = false;
        return this.$q.reject(err);
      });
    return this._versionCheckPromise;
  }

  _showPostUpdateMessage() {
    const delay = 1000;
    return this.$timeout(() => {
      return this.showAppVersionUpdateCompleteModal()
        .then(result => {
          this.$timeout(() => this._versionCheckAndUpdate());
          return result;
        })
        .catch(err => {
          this.LogService.debug(`StaffController._showPostUpdateMessage() got error:`, err, 'not retrying');
          return this.$q.reject(err);
        })
    }, delay);
  }

  _setupSlowRequestLogging() {
    if (this.LOG_SLOW_REQUEST_THRESHOLD > 0) {
      const staffAppLogSlowRequest = (method, url, response, duration) => {
        this.LogService.warn(`Logging slow request ${method} ${url} duration ${duration} ms`);
        this.AnalyticsService.trackEvent('SLOW_REQUEST', {
          method,
          url,
          duration,
        });
      };
      this.APIService.addSlowRequestCallback(this.LOG_SLOW_REQUEST_THRESHOLD, staffAppLogSlowRequest);
    } else {
      this.LogService.warn(`LOG_SLOW_REQUEST_THRESHOLD set to 0; not logging slow requests`);
    }
  }

  // Refresh the dashboard when the refresh icon is clicked.
  refreshDashboard() {
    return this._versionCheckAndUpdate()
      .catch(err => {
        this.LogService.error("Swallowing error with version check:", err);
      })
      .then(() => {
        this.$scope.$emit('sync');
      });
  }

  /**
   * Locale handling
   */
  get availableLocales() {

  }

  get themeName() {
    return this.ThemeSwitcherService.translatedThemeName;
  }

  get oppositeThemeName() {
    return this.ThemeSwitcherService.oppositeThemeName;
  }

  toggleTheme() {
    return this.ThemeSwitcherService.toggleTheme();
  }

  get isDarkThemeActive() {
    return !!this._isDarkThemeActive;
  }

  toggleDarkTheme() {
    this._isDarkThemeActive = !this.isDarkThemeActive;
    if (this.isDarkThemeActive) {
    }
  }

  logout() {
    this.$translate('CONFIRM_LOGOUT')
      .then(logout_msg => this.showConfirmModal(logout_msg))
        .then(() => {
          this.$scope.$emit('hideEditProfile');
          this.$scope.$emit('logout');
        });
  }

  currentYear() {
    return new Date().getFullYear();
  }
}

export default FieldStaffAppController;
